import {
  CHANGE_KINDS,
  type ChangeKind,
  type ChangeRequest,
  type ChangeStatus,
} from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { z } from 'zod';
import {
  ExpiryChip,
  ChangeStatus as StatusCell,
} from '@/components/approvals/change-parts';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { Money } from '@/components/primitives/money';
import {
  OpenRecord,
  PersonCell,
  SummaryBand,
} from '@/components/queue/queue-parts';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  changeKindLabel,
  changeStatusWords,
  checkersLabel,
  EXPIRY_WARNING_MS,
} from '@/lib/approvals';
import { relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';
import { adminQuery, useKeysetList } from '@/lib/query';

const VIEWS = [
  { key: 'waiting', label: 'Waiting for you' },
  { key: 'pending', label: 'Waiting, all' },
  { key: 'mine', label: 'Proposed by me' },
  { key: 'all', label: 'All' },
] as const;

type View = (typeof VIEWS)[number]['key'];

const STATUSES: readonly ChangeStatus[] = [
  'pending',
  'applied',
  'rejected',
  'withdrawn',
  'expired',
  'failed',
];

const searchSchema = z.object({
  view: z
    .enum(['waiting', 'pending', 'mine', 'all'])
    .catch('waiting')
    .default('waiting'),
  status: z
    .enum(STATUSES as unknown as [ChangeStatus, ...ChangeStatus[]])
    .optional(),
  kind: z.enum(CHANGE_KINDS).optional(),
});

type ChangeSearch = z.infer<typeof searchSchema>;

/** The queue: what waits for me, what waits at all, what I proposed, everything. */
export const Route = createFileRoute('/_app/approvals/')({
  validateSearch: searchSchema,
  component: ApprovalsPage,
});

/** "Waiting, all" is every request that is still pending, whoever must decide it. */
function toArgs(search: ChangeSearch) {
  return {
    view: search.view === 'pending' ? ('all' as const) : search.view,
    status: search.view === 'pending' ? ('pending' as const) : search.status,
    kind: search.kind,
  };
}

const column = createColumnHelper<ChangeRequest>();

const COLUMNS = [
  column.accessor('expires_at', {
    header: 'Expires',
    cell: ({ row }) =>
      row.original.status === 'pending' ? (
        <ExpiryChip change={row.original} size="sm" />
      ) : (
        <span className="whitespace-nowrap text-muted-foreground">
          {relativeTime(row.original.decided_at ?? row.original.created_at)}
        </span>
      ),
  }),
  column.accessor('title', {
    header: 'Request',
    cell: ({ row }) => (
      <OpenRecord
        sheetRef={{ type: 'change', id: row.original.id }}
        label={`Open request: ${row.original.title}`}
        className="line-clamp-2 max-w-md font-medium hover:underline"
      >
        {row.original.title}
      </OpenRecord>
    ),
  }),
  column.accessor((row) => changeKindLabel(row.kind), {
    id: 'kind',
    header: 'Kind',
  }),
  column.accessor((row) => row.amount_minor ?? -1, {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) =>
      row.original.amount_minor === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="block text-right">
          <Money
            minor={row.original.amount_minor}
            currency={row.original.currency ?? homeCurrency()}
          />
        </span>
      ),
  }),
  column.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.customer?.name ?? '' },
    cell: ({ row }) =>
      row.original.customer ? (
        <OpenRecord
          sheetRef={{ type: 'cust', id: row.original.customer.id }}
          label={`Open customer ${row.original.customer.name ?? ''}`.trim()}
        >
          <PersonCell customer={row.original.customer} />
        </OpenRecord>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  }),
  column.accessor((row) => row.maker.name, {
    id: 'maker',
    header: 'Proposed by',
  }),
  column.accessor((row) => checkersLabel(row), {
    id: 'checkers',
    header: 'Checkers',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">{info.getValue()}</span>
    ),
  }),
  column.accessor((row) => changeStatusWords(row.status).label, {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusCell change={row.original} size="sm" />,
  }),
];

const EMPTY: Record<View, { title: string; body?: string }> = {
  waiting: {
    title: 'Nothing is waiting for you.',
    body: 'Requests that need your approval appear here.',
  },
  pending: { title: 'No requests are waiting for approval.' },
  mine: {
    title: 'You have not proposed any requests.',
    body: 'Requests you propose appear here until someone decides them.',
  },
  all: {
    title: 'No requests yet.',
    body: 'Money, limit and configuration changes appear here once someone proposes them.',
  },
};

function ApprovalsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sheetActions = useSheetActions();
  const args = toArgs(search);
  const list = useKeysetList('changes.list', args, {
    rows: (data) => data.rows,
  });
  // The summary reads what waits at all, whatever tab is open.
  const waitingAll = useQuery(
    adminQuery('changes.list', { view: 'all', status: 'pending' }),
  );
  const counts = list.data?.counts ?? waitingAll.data?.counts;
  const set = (patch: Partial<ChangeSearch>) =>
    void navigate({ search: { ...search, ...patch } });
  const showStatus = search.view === 'mine' || search.view === 'all';
  const filtered = Boolean(search.kind || (showStatus && search.status));

  const waiting = waitingAll.data;
  const more = waiting?.next_before ? '+' : '';
  const expiring = waiting?.rows.filter(
    (row) => Date.parse(row.expires_at) - Date.now() < EXPIRY_WARNING_MS,
  ).length;

  return (
    <>
      <PageHeader
        title="Approvals"
        description="Every money, limit and configuration change waits here for a second person."
      />
      <SummaryBand
        label="Approval figures"
        cells={[
          {
            label: 'Waiting for you',
            value: counts?.waiting,
            onSelect: () => set({ view: 'waiting', status: undefined }),
            active: search.view === 'waiting',
          },
          {
            label: 'Expiring within 12 h',
            value: expiring === undefined ? undefined : `${expiring}${more}`,
            tone: 'warning',
            onSelect: () => set({ view: 'pending', status: undefined }),
          },
          {
            label: 'Waiting, all',
            value: waiting ? `${waiting.rows.length}${more}` : undefined,
            onSelect: () => set({ view: 'pending', status: undefined }),
            active: search.view === 'pending',
          },
          {
            label: 'Proposed by me, waiting',
            value: counts?.mine,
            onSelect: () => set({ view: 'mine' }),
            active: search.view === 'mine',
          },
        ]}
      />
      <TabLinks
        tabs={VIEWS.map((view) => ({
          key: view.key,
          label:
            (view.key === 'waiting' || view.key === 'mine') && counts
              ? `${view.label} (${counts[view.key]})`
              : view.label,
        }))}
        active={search.view}
        render={(tab, domProps) => (
          <Link
            {...domProps}
            to="/approvals"
            search={{ ...search, view: tab.key as View, status: undefined }}
          />
        )}
      />
      <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        {showStatus ? (
          <Select
            label="Status"
            className="w-full sm:w-44"
            value={search.status ?? ''}
            onChange={(event) =>
              set({
                status: (event.target.value || undefined) as
                  | ChangeStatus
                  | undefined,
              })
            }
          >
            <option value="">Any</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {changeStatusWords(status).label}
              </option>
            ))}
          </Select>
        ) : null}
        <Select
          label="Kind"
          className="w-full sm:w-64"
          value={search.kind ?? ''}
          onChange={(event) =>
            set({
              kind: (event.target.value || undefined) as ChangeKind | undefined,
            })
          }
        >
          <option value="">Any</option>
          {CHANGE_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {changeKindLabel(kind)}
            </option>
          ))}
        </Select>
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => set({ status: undefined, kind: undefined })}
          >
            Clear filters
          </Button>
        ) : null}
      </div>
      <QueryState
        query={list.query}
        label="requests"
        skeleton={<SkeletonTable columns={8} />}
      >
        {() =>
          list.rows?.length === 0 ? (
            <EmptyState
              variant={filtered ? 'filtered' : 'clear'}
              title={
                filtered
                  ? 'No requests match these filters.'
                  : EMPTY[search.view].title
              }
              body={filtered ? undefined : EMPTY[search.view].body}
            />
          ) : (
            <DataTable
              id="approvals"
              columns={COLUMNS}
              data={list.rows}
              more={list.more}
              rowLabel={(row) => `Open request ${row.title}`}
              onRowClick={(row, rowEl) =>
                sheetActions.open(
                  { type: 'change', id: row.id },
                  { fromRowEl: rowEl.querySelector('button') ?? rowEl },
                )
              }
            />
          )
        }
      </QueryState>
    </>
  );
}
